import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import type { Item, WheelbarrowItem } from '../types';

interface WheelbarrowState {
  items: WheelbarrowItem[];
  totalPence: number;
}

type WheelbarrowAction =
  | { type: 'ADD_ITEM'; payload: { item: Item; days: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_DAYS'; payload: { itemId: string; days: number } }
  | { type: 'CLEAR' };

interface WheelbarrowContextType {
  state: WheelbarrowState;
  addItem: (item: Item, days: number) => void;
  removeItem: (itemId: string) => void;
  updateDays: (itemId: string, days: number) => void;
  clearWheelbarrow: () => void;
}

const WheelbarrowContext = createContext<WheelbarrowContextType | undefined>(undefined);

const wheelbarrowReducer = (state: WheelbarrowState, action: WheelbarrowAction): WheelbarrowState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.item.id === action.payload.item.id);
      if (existingItem) {
        return state;
      }
      const newItems = [...state.items, { item: action.payload.item, days: action.payload.days }];
      return {
        items: newItems,
        totalPence: calculateTotalPence(newItems),
      };
    }
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.item.id !== action.payload);
      return {
        items: newItems,
        totalPence: calculateTotalPence(newItems),
      };
    }
    case 'UPDATE_DAYS': {
      const newItems = state.items.map(item =>
        item.item.id === action.payload.itemId
          ? { ...item, days: action.payload.days }
          : item
      );
      return {
        items: newItems,
        totalPence: calculateTotalPence(newItems),
      };
    }
    case 'CLEAR':
      return {
        items: [],
        totalPence: 0,
      };
    default:
      return state;
  }
};

const calculateTotalPence = (items: WheelbarrowItem[]): number => {
  return items.reduce((total, item) => total + (Math.round(item.item.dailyRateGBP * 100) * item.days), 0);
};

export const WheelbarrowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(wheelbarrowReducer, { items: [], totalPence: 0 });

  const addItem = (item: Item, days: number) => {
    dispatch({ type: 'ADD_ITEM', payload: { item, days } });
  };

  const removeItem = (itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
  };

  const updateDays = (itemId: string, days: number) => {
    dispatch({ type: 'UPDATE_DAYS', payload: { itemId, days } });
  };

  const clearWheelbarrow = () => {
    dispatch({ type: 'CLEAR' });
  };

  return (
    <WheelbarrowContext.Provider
      value={{ state, addItem, removeItem, updateDays, clearWheelbarrow }}
    >
      {children}
    </WheelbarrowContext.Provider>
  );
};

export const useWheelbarrow = () => {
  const context = useContext(WheelbarrowContext);
  if (context === undefined) {
    throw new Error('useWheelbarrow must be used within a WheelbarrowProvider');
  }
  return context;
}; 